import { z } from "zod";

import { isLinkedinCompanyUrl } from "@signalist/shared-types";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { ListStatus } from "@/enum/list.enum";

import { LiCompanyActivityTimeframe } from "@/enum/licompanyactivity.enum";

import { LiCompanyActivityEntrySchema } from "./liCompanyActivityEntry";

const { REQUIRED } = ERROR_TYPE;

export const LiCompanyActivitySchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	url: z.string().min(4, { message: REQUIRED }).trim(),
	scrapeFollowers: z.boolean(),
	timeFrame: z.number().optional(),
	activeStatus: z.nativeEnum(ListStatus),
	liCompanyActivityEntries: z.array(LiCompanyActivityEntrySchema).optional(),
	timeframe: z.nativeEnum(LiCompanyActivityTimeframe).optional(),
	amount: z.number().optional(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const CreateLiCompanyActivitySchema = z
	.object({
		id: z.number().optional(),
		name: z.string().min(4, { message: REQUIRED }).trim(),
		// PREC-4461: the run reads the company id out of this URL, so a profile (or any other
		// non-company) page is refused here as well as in the API DTO.
		url: z
			.string()
			.trim()
			.min(4, { message: REQUIRED })
			.refine((value) => value.length === 0 || isLinkedinCompanyUrl(value), {
				message: ERROR_TYPE.INVALID_LINKEDIN_COMPANY_URL,
			}),
		scrapeFollowers: z.boolean(),
		timeframe: z.nativeEnum(LiCompanyActivityTimeframe).optional().nullable(),
		amount: z.number().max(2000, { message: ERROR_TYPE.MAX_AMOUNT_EXCEEDED }).optional().nullable(),
		listId: z.number().optional(),
		plannedScrapingUserId: z.number().optional().nullable(),
	})
	.refine(
		(values) => {
			if (!values.timeframe && !values.amount) {
				return false;
			}

			return true;
		},
		{
			message: REQUIRED,
			path: [],
		}
	);

export const UpdateLiCompanyActivitySchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	// Kept in step with the create schema: the update modal uses the create schema today, and a
	// future switch must not lose the company-page refusal (PREC-4461).
	url: z
		.string()
		.trim()
		.min(4, { message: REQUIRED })
		.refine((value) => value.length === 0 || isLinkedinCompanyUrl(value), {
			message: ERROR_TYPE.INVALID_LINKEDIN_COMPANY_URL,
		}),
	scrapeFollowers: z.boolean(),
	timeFrame: z.number().optional(),
	listId: z.number().optional(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const LiCompanyActivityResponseSchema = z.object({
	data: z.array(LiCompanyActivitySchema),
	meta: MetaDataSchema,
});

export type LiCompanyActivity = z.infer<typeof LiCompanyActivitySchema>;

export type CreateLiCompanyActivity = z.infer<typeof CreateLiCompanyActivitySchema>;

export type UpdateLiCompanyActivity = z.infer<typeof UpdateLiCompanyActivitySchema>;

export type LiCompanyActivityResponse = z.infer<typeof LiCompanyActivityResponseSchema>;
